import { formatAndList } from './egsUtils';
import { EGS_SELL_SOURCE_CURRENCY } from './egsWriteCurrency';

// Each one stops the write for its room rate rather than degrading it.
export type EgsApplyBlocker =
  | 'archive_disagreement'
  | 'conflicting_slots'
  | 'currency_mismatch'
  | 'fx_rates_loading'
  | 'fx_rates_unavailable'
  | 'missing_cost_price'
  | 'missing_fx_rate'
  | 'multiple_existing_records'
  | 'payable_at_property_conflict'
  | 'payable_at_property_mismatch';

export interface EgsRefusalContext {
  guestTypes: Array<string>;
  writeCurrency: string;
}

/** Gates supply a question; refusals supply a message. `wait` means neither editor can fix the issue. */
export type EgsBlockerDefinition =
  | { kind: 'gate' }
  | {
      fixOn: 'roomRate' | 'salesforce' | 'wait';
      kind: 'refusal';
      message: (context: EgsRefusalContext) => string;
      severity: 'error' | 'info';
    };

/** Falls back to the anonymous wording, so a missing slot list costs a name rather than the whole sentence. */
function describeSalesforceRows(guestTypes: Array<string>) {
  if (!guestTypes.length) {
    return 'A Salesforce row has';
  }

  return guestTypes.length === 1
    ? `The ${guestTypes[0]} row from Salesforce has`
    : `The ${formatAndList(guestTypes)} rows from Salesforce have`;
}

function describeGuestTypes(guestTypes: Array<string>) {
  return guestTypes.length === 1 ? `the ${guestTypes[0]}` : formatAndList(guestTypes);
}

export const EGS_BLOCKERS: Record<EgsApplyBlocker, EgsBlockerDefinition> = {
  archive_disagreement: { kind: 'gate' },
  conflicting_slots: { kind: 'gate' },
  currency_mismatch: {
    fixOn: 'roomRate',
    kind: 'refusal',
    message: () =>
      'This room rate keeps its surcharges in a different currency from the one this offer was contracted in. Mixing the two would change the guest types we are leaving alone. Note the current amounts, delete the surcharge on the room rate, then apply again.',
    severity: 'error',
  },
  fx_rates_loading: {
    fixOn: 'wait',
    kind: 'refusal',
    message: ({ guestTypes, writeCurrency }) =>
      `Exchange rates are still loading, so ${describeGuestTypes(guestTypes)} cannot be converted from ${EGS_SELL_SOURCE_CURRENCY} into ${writeCurrency} yet. This clears on its own once they arrive.`,
    severity: 'info',
  },
  fx_rates_unavailable: {
    fixOn: 'wait',
    kind: 'refusal',
    message: ({ guestTypes, writeCurrency }) =>
      `Exchange rates could not be loaded, so ${describeGuestTypes(guestTypes)} cannot be converted from ${EGS_SELL_SOURCE_CURRENCY} into ${writeCurrency}. Reload the page, then apply again.`,
    severity: 'error',
  },
  missing_cost_price: {
    fixOn: 'salesforce',
    kind: 'refusal',
    message: ({ guestTypes }) =>
      `${describeSalesforceRows(guestTypes)} a price but no Cost Price, so margin would look higher than it is. Add the cost in Salesforce, then apply again.`,
    severity: 'error',
  },
  missing_fx_rate: {
    fixOn: 'roomRate',
    kind: 'refusal',
    message: ({ guestTypes, writeCurrency }) =>
      `Salesforce prices ${describeGuestTypes(guestTypes)} in ${EGS_SELL_SOURCE_CURRENCY} and we have no exchange rate for ${writeCurrency}, the currency this offer was contracted in, so applying would store ${EGS_SELL_SOURCE_CURRENCY} amounts instead. Enter the amounts on the room rate yourself.`,
    severity: 'error',
  },
  multiple_existing_records: {
    fixOn: 'roomRate',
    kind: 'refusal',
    message: () =>
      'This room rate has more than one surcharge saved and we cannot tell which one a booking uses. Delete the ones that are no longer live, leaving one, then apply again.',
    severity: 'error',
  },
  payable_at_property_conflict: { kind: 'gate' },
  // Asked through the same question as the conflict above: one flag per room rate, one answer to give.
  payable_at_property_mismatch: { kind: 'gate' },
};

export function isGateBlocker(blocker: EgsApplyBlocker) {
  return EGS_BLOCKERS[blocker].kind === 'gate';
}

/** The definition only where it is a refusal, so a caller cannot render a message for an answerable gate. */
export function getEgsRefusal(blocker: EgsApplyBlocker | undefined) {
  if (!blocker) {
    return undefined;
  }

  const definition = EGS_BLOCKERS[blocker];

  return definition.kind === 'refusal' ? definition : undefined;
}
